import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth-context.jsx';
import appMenu from '../../app/app-menu.js';
import { useCalls } from '../../modules/calls/context/calls-context.jsx';
import { filterCallsMenu } from '../../modules/calls/model/calls.model.js';
import DropdownNotification from '../../components/header/dropdown/notification.jsx';
import { useComplianceMode } from '../../modules/system-navigation/context/compliance-mode-context.jsx';
import { filterMenuItems } from '../../modules/system-navigation/model/compliance-visibility.js';
import { filterMenuItemsByPermissions } from '../../modules/system-navigation/model/permission-visibility.js';
import LanguageSwitcher from '../ui/language-switcher/language-switcher.jsx';
import { useTranslation } from 'react-i18next';
import { useHelp } from '../../context/help-context.jsx';
import HelpDrawer from '../ui/help-drawer/help-drawer.jsx';
import { getRuntimeConfig } from '../../lib/runtime-config.js';
import { BrandedMark, normalizeBranding } from '../branding/branding.jsx';
import UiScaleControl from '../ui-scale/ui-scale-control.jsx';
import {
  applyUiScaleToDocument,
  getStoredUiScale,
  setStoredUiScale,
} from '../ui-scale/ui-scale.js';

function hasActivePath(item, pathname) {
  if (item.to) {
    if (pathname === item.to) {
      return true;
    }

    if (pathname.startsWith(`${item.to}/`)) {
      return true;
    }
  }

  if (!Array.isArray(item.children)) {
    return false;
  }

  return item.children.some((child) => hasActivePath(child, pathname));
}

function collectActiveGroupKeys(item, pathname, keys = []) {
  if (!Array.isArray(item.children) || item.children.length === 0) {
    return keys;
  }

  const currentKey = item.key || item.label;
  if (hasActivePath(item, pathname)) {
    keys.push(currentKey);
  }

  item.children.forEach((child) => collectActiveGroupKeys(child, pathname, keys));
  return keys;
}

function AppShellLayout({ children }) {
  const { enabled: callsEnabled } = useCalls();
  const { user, permissions, logout } = useAuth();
  const isTenantRestrictedMode = Boolean(user?.tenantRestrictedMode);
  const { isFinancialRegulation } = useComplianceMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isMiniSidebar, setIsMiniSidebar] = React.useState(() => {
    try {
      return localStorage.getItem('app_sidebartype') === 'mini-sidebar';
    } catch {
      return false;
    }
  });
  const [theme, setTheme] = React.useState(() => {
    try {
      return localStorage.getItem('app_theme') || document.documentElement.getAttribute('data-bs-theme') || 'light';
    } catch {
      return document.documentElement.getAttribute('data-bs-theme') || 'light';
    }
  });
  const [uiScale, setUiScale] = React.useState(getStoredUiScale);
  const [openGroups, setOpenGroups] = React.useState({});
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false);
  const previousAttrsRef = React.useRef(null);
  const profileMenuRef = React.useRef(null);
  const { t } = useTranslation();
  const { toggleHelp } = useHelp();
  const branding = React.useMemo(() => normalizeBranding(getRuntimeConfig().branding), []);
  const visibleMenu = React.useMemo(
    () => appMenu
      .map((group) => ({
        ...group,
        items: filterMenuItemsByPermissions(
          filterCallsMenu(filterMenuItems(group.items || [], isFinancialRegulation), callsEnabled),
          permissions,
        ),
      }))
      .filter((group) => group.items.length > 0),
    [isFinancialRegulation, permissions, callsEnabled],
  );

  const isSidebarDrawerViewport = React.useCallback(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1299.98px)').matches,
    [],
  );

  React.useEffect(() => {
    previousAttrsRef.current = {
      htmlLayout: document.documentElement.getAttribute('data-layout'),
      htmlTheme: document.documentElement.getAttribute('data-bs-theme'),
      htmlColorTheme: document.documentElement.getAttribute('data-color-theme'),
      htmlUiScale: document.documentElement.getAttribute('data-ui-scale'),
      htmlUiScaleVariable: document.documentElement.style.getPropertyValue('--app-ui-scale'),
      bodySidebarType: document.body.getAttribute('data-sidebartype'),
      bodyHadMiniSidebarClass: document.body.classList.contains('mini-sidebar'),
    };

    document.documentElement.setAttribute('data-layout', 'vertical');
    document.documentElement.setAttribute('data-color-theme', 'Blue_Theme');

    let link = document.getElementById('app-materialpro-css');
    let createdLink = false;
    if (!link) {
      link = document.createElement('link');
      link.id = 'app-materialpro-css';
      link.rel = 'stylesheet';
      link.href = '/assets/css/styles.css';
      document.head.appendChild(link);
      createdLink = true;
    }

    return () => {
      const previous = previousAttrsRef.current;
      if (!previous) {
        return;
      }

      if (previous.htmlLayout) {
        document.documentElement.setAttribute('data-layout', previous.htmlLayout);
      } else {
        document.documentElement.removeAttribute('data-layout');
      }

      if (previous.htmlTheme) {
        document.documentElement.setAttribute('data-bs-theme', previous.htmlTheme);
      } else {
        document.documentElement.removeAttribute('data-bs-theme');
      }

      if (previous.htmlColorTheme) {
        document.documentElement.setAttribute('data-color-theme', previous.htmlColorTheme);
      } else {
        document.documentElement.removeAttribute('data-color-theme');
      }

      if (previous.htmlUiScale) {
        document.documentElement.setAttribute('data-ui-scale', previous.htmlUiScale);
      } else {
        document.documentElement.removeAttribute('data-ui-scale');
      }

      if (previous.htmlUiScaleVariable) {
        document.documentElement.style.setProperty('--app-ui-scale', previous.htmlUiScaleVariable);
      } else {
        document.documentElement.style.removeProperty('--app-ui-scale');
      }

      if (previous.bodySidebarType) {
        document.body.setAttribute('data-sidebartype', previous.bodySidebarType);
      } else {
        document.body.removeAttribute('data-sidebartype');
      }

      if (previous.bodyHadMiniSidebarClass) {
        document.body.classList.add('mini-sidebar');
      } else {
        document.body.classList.remove('mini-sidebar');
      }

      if (createdLink && link?.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, []);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    try {
      localStorage.setItem('app_theme', theme);
    } catch {
      // ignore storage errors
    }
  }, [theme]);

  React.useEffect(() => {
    const normalized = applyUiScaleToDocument(uiScale);
    setStoredUiScale(normalized);
  }, [uiScale]);

  React.useEffect(() => {
    const sidebartype = isMiniSidebar ? 'mini-sidebar' : 'full';
    document.body.setAttribute('data-sidebartype', sidebartype);
    document.body.classList.toggle('mini-sidebar', isMiniSidebar);
    try {
      localStorage.setItem('app_sidebartype', sidebartype);
    } catch {
      // ignore storage errors
    }
  }, [isMiniSidebar]);

  React.useEffect(() => {
    const onResize = () => {
      if (!isSidebarDrawerViewport()) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [isSidebarDrawerViewport]);

  React.useEffect(() => {
    const nextState = {};
    visibleMenu.forEach((group) => {
      group.items.forEach((item) => {
        collectActiveGroupKeys(item, location.pathname).forEach((key) => {
          nextState[key] = true;
        });
      });
    });
    setOpenGroups((prev) => ({ ...prev, ...nextState }));
  }, [location.pathname, visibleMenu]);

  React.useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, []);

  const renderNavItem = (item, level = 0) => {
    if (item.children?.length) {
      const key = item.key || item.label;
      const isOpen = Boolean(openGroups[key]);
      const childListClassName = level === 0 ? 'collapse first-level' : 'collapse first-level ms-3';

      return (
        <li key={item.key || item.label} className="sidebar-item">
          <button
            type="button"
            className={`sidebar-link has-arrow app-menu-button${isOpen ? ' is-open' : ''}`}
            onClick={() => {
              setOpenGroups((prev) => ({
                ...prev,
                [key]: !prev[key],
              }));
            }}
            aria-expanded={isOpen}
          >
            <span className="d-flex">
              <i className={`${item.iconClass || 'ti ti-folder'} aside-icon`} aria-hidden="true"></i>
            </span>
            <span className="hide-menu">{t(item.label)}</span>
          </button>

          <ul className={`${childListClassName}${isOpen ? ' show' : ''}`}>
            {item.children.map((child) => renderNavItem(child, level + 1))}
          </ul>
        </li>
      );
    }

    return (
      <li key={item.to || item.label} className="sidebar-item">
        <NavLink
          to={item.to || '/dashboard'}
          end={Boolean(item.end)}
          className={({ isActive }) => `sidebar-link${level > 0 ? ' sublink' : ''}${isActive ? ' active' : ''}${item.disabled ? ' app-disabled' : ''}`}
          onClick={(event) => {
            if (item.disabled) {
              event.preventDefault();
            }
            if (isSidebarDrawerViewport()) {
              setIsSidebarOpen(false);
            }
          }}
        >
          {level > 0 ? (
            <div className="round-16 d-flex align-items-center justify-content-center">
              <i className="ti ti-point text-dark fs-2" aria-hidden="true"></i>
            </div>
          ) : item.iconClass ? (
            <span className="d-flex">
              <i className={`${item.iconClass} aside-icon`} aria-hidden="true"></i>
            </span>
          ) : null}
          <span className="hide-menu">{t(item.label)}</span>
        </NavLink>
      </li>
    );
  };

  const handleSidebarToggle = () => {
    if (isSidebarDrawerViewport()) {
      setIsSidebarOpen((prev) => !prev);
      return;
    }

    setIsMiniSidebar((prev) => !prev);
  };

  const handleThemeToggle = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
    <div
      id="main-wrapper"
      className={`app-main-wrapper${isSidebarOpen ? ' show-sidebar' : ''}`}
      data-sidebartype={isMiniSidebar ? 'mini-sidebar' : 'full'}
    >
      <aside id="admin-sidebar" className="left-sidebar with-vertical">
        <nav className="sidebar-nav scroll-sidebar">
          <ul id="sidebarnav">
            {visibleMenu.map((group) => (
              <React.Fragment key={group.section}>
                <li className="nav-small-cap">
                  <i className="fa fa-ellipsis nav-small-cap-icon fs-4" aria-hidden="true"></i>
                  <span className="hide-menu">{t(group.section)}</span>
                </li>
                {group.items.map((item) => renderNavItem(item))}
              </React.Fragment>
            ))}
          </ul>
        </nav>

      </aside>

      <div className="page-wrapper">
        <header className="topbar rounded-0 border-0 bg-primary">
          <nav className="navbar navbar-expand-lg px-lg-0 px-3 py-0">
            <div className="d-none d-lg-block">
              <div className="brand-logo d-flex align-items-center justify-content-between">
                <span className="text-nowrap logo-img d-flex align-items-center">
                  <BrandedMark
                    branding={branding}
                    className="crm-wordmark-inverse crm-wordmark-compact"
                    imageClassName="crm-brand-logo-header"
                    imageHeight={32}
                  />
                </span>
              </div>
            </div>

            <ul className="navbar-nav gap-2">
              <li className="nav-item nav-icon-hover-bg rounded-circle">
                <button
                  type="button"
                  className="nav-link nav-icon-hover sidebartoggler border-0 bg-transparent text-white"
                  onClick={handleSidebarToggle}
                  aria-label={t('header.menu', 'Menu')}
                  aria-expanded={isSidebarOpen}
                  aria-controls="admin-sidebar"
                >
                  <i className="ti ti-menu-2"></i>
                </button>
              </li>
              <li className="nav-item d-none d-lg-block search-box nav-icon-hover-bg rounded-circle">
                <span className="nav-link nav-icon-hover d-none d-md-flex">
                  <i className="ti ti-search"></i>
                </span>
              </li>
              <li className="nav-item d-none d-lg-block nav-icon-hover-bg rounded-circle">
                <span className="nav-link nav-icon-hover">
                  <i className="ti ti-layout-grid"></i>
                </span>
              </li>
            </ul>

            <div className="d-flex align-items-center justify-content-end py-2 py-lg-0 ms-auto">
              <div className="d-flex align-items-center justify-content-between">
                <ul className="navbar-nav gap-2 flex-row ms-auto align-items-center justify-content-center">
                  <LanguageSwitcher />
                  <UiScaleControl value={uiScale} onChange={setUiScale} />
                  <li className="nav-item nav-icon-hover-bg rounded-circle">
                    <button
                      type="button"
                      className="nav-link nav-icon-hover text-white border-0 bg-transparent"
                      onClick={handleThemeToggle}
                    >
                      <i className={theme === 'dark' ? 'ti ti-sun' : 'ti ti-moon'}></i>
                    </button>
                  </li>
                  <DropdownNotification />
                  <li className="nav-item nav-icon-hover-bg rounded-circle">
                    <span className="nav-link nav-icon-hover text-white"><i className="ti ti-mail"></i></span>
                  </li>
                  <li className="nav-item nav-icon-hover-bg rounded-circle">
                    <button
                      type="button"
                      className="nav-link nav-icon-hover text-white border-0 bg-transparent"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        toggleHelp();
                      }}
                      aria-label={t('header.help')}
                    >
                      <i className="ti ti-help"></i>
                    </button>
                  </li>
                  <li className={`nav-item dropdown${isProfileMenuOpen ? ' show' : ''}`} ref={profileMenuRef}>
                    <button
                      type="button"
                      className="nav-link nav-icon-hover profile-thumb border-0 bg-transparent"
                      onClick={() => {
                        setIsProfileMenuOpen((prev) => !prev);
                      }}
                      aria-label={t('header.profileMenu', 'Profile menu')}
                      aria-expanded={isProfileMenuOpen}
                    >
                      <i className="ti ti-user fs-6" aria-hidden="true"></i>
                    </button>
                    <div className={`dropdown-menu dropdown-menu-end${isProfileMenuOpen ? ' show' : ''}`}>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          navigate('/security');
                        }}
                      >
                        {t('header.security', 'Security')}
                      </button>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          handleLogout();
                        }}
                      >
                        {t('header.logout')}
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        </header>

        <div className="body-wrapper">
          {isTenantRestrictedMode ? (
            <div className="app-restricted-mode-banner" role="status">
              <i className="ti ti-alert-triangle" aria-hidden="true"></i>
              <span>{t('header.restrictedModeBanner')}</span>
            </div>
          ) : null}
          <div className="container-fluid">{children}</div>
        </div>
      </div>

    </div>
    {isSidebarOpen ? (
      <button
        type="button"
        className="app-sidebar-backdrop"
        aria-label={t('header.closeMenu', 'Close menu')}
        onClick={() => setIsSidebarOpen(false)}
      />
    ) : null}
    <HelpDrawer />
    </>
  );
}

export default AppShellLayout;
